// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import getWindow from '../../../../utils/get-window'
import { Card, Field, FieldGroup, Label, Input, Select } from '../../../../atoms'
import Callout from '../../../callout'
import { lineOptions } from './line-options'
import { calculation } from './calculation'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

const stringToFloat = (s) => (isNaN(parseFloat(s)) ? 0 : parseFloat(s))

const frequencyMap = {
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
}

const SavingsCalculator = ({
  params: {
    startingAmount: startingAmountParam,
    targetAmount: targetAmountParam,
    contribution: contributionParam,
    contributionFrequency: contributionFrequencyParam,
    interestRate: interestRateParam,
  },
  warningText,
}) => {
  const [startingAmount, setStartingAmount] = useState(startingAmountParam)
  const [targetAmount, setTargetAmount] = useState(targetAmountParam)
  const [contribution, setContribution] = useState(contributionParam)
  const [contributionFrequency, setContributionFrequency] = useState(contributionFrequencyParam)
  const [contributionString, setContributionString] = useState(contributionParam.toString())
  const [interestRate, setInterestRate] = useState(interestRateParam)
  const [savingTime, setSavingTime] = useState(null)
  const [interestSaved, setInterestSaved] = useState(null)
  const [dateReachedGoal, setDateReachedGoal] = useState(null)
  const [chartData, setChartData] = useState(null)

  useEffect(() => {
    setContribution(parseFloat(contributionString))
  }, [contributionString])

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(getWindow().location?.search)
    const entries = Object.fromEntries(urlSearchParams.entries())
    const entriesParsed = Object.entries(entries).reduce(
      (obj, [key, val]) => ({ ...obj, [key]: parseInt(val) }),
      {},
    )
    if (entriesParsed.startingAmount) {
      setStartingAmount(entriesParsed.startingAmount)
    }
    if (entriesParsed.targetAmount) {
      setTargetAmount(entriesParsed.targetAmount)
    }
    if (entriesParsed.contributionString) {
      setContributionString(entriesParsed.contributionString)
    }
    if (entriesParsed.contributionFrequency) {
      setContributionFrequency(entriesParsed.contributionFrequency)
    }
    if (entriesParsed.interestRate) {
      setInterestRate(entriesParsed.interestRate)
    }
  }, [])

  useEffect(() => {
    const calculationResult = calculation({
      startingAmount,
      targetAmount,
      contribution,
      contributionFrequency,
      interestRate,
    })
    setDateReachedGoal(calculationResult.dateReachedGoal)
    setInterestSaved(calculationResult.interestSaved)
    setSavingTime(calculationResult.savingTime)
    setChartData({
      steps: calculationResult.steps,
      savingsData: calculationResult.savingsData,
    })
  }, [startingAmount, targetAmount, contribution, contributionFrequency, interestRate])

  return (
    <div className='SavingsCalculator'>
      <Card variant='plum'>
        <Field stack={true}>
          <Label>Starting amount/ How much do you have now?</Label>
          <Input
            value={startingAmount}
            onChange={(e) => {
              setStartingAmount(e.target.value)
            }}
            type={'text'}
            prefix={'£'}
            format={'thousands'}
          />
        </Field>
        <Field stack={true}>
          <Label>Target amount/ How much are you looking to save?</Label>
          <Input
            value={targetAmount}
            onChange={(e) => {
              setTargetAmount(e.target.value)
            }}
            type={'text'}
            prefix={'£'}
            format={'thousands'}
          />
        </Field>
        <Field stack={true}>
          <Label>How much can you afford to save?</Label>
          <FieldGroup stack={true}>
            <Input
              value={contributionString}
              onChange={(e) => {
                setContributionString(e.target.value)
              }}
              type={'text'}
              prefix={'£'}
              format={'thousands'}
            />
            <Select
              name='contribution-frequency'
              value={contributionFrequency}
              options={[
                { value: 'weekly', text: 'Weekly' },
                { value: 'monthly', text: 'Monthly' },
                { value: 'yearly', text: 'Yearly' },
              ]}
              onChange={(e) => {
                setContributionFrequency(e.target.value)
              }}
            />
          </FieldGroup>
        </Field>

        <Field stack={true}>
          <Label>Interest rate (%)</Label>
          <Input
            value={interestRate}
            onChange={(e) => setInterestRate(stringToFloat(e.target.value))}
            type={'text'}
          />
        </Field>

        {savingTime !== null && interestSaved !== null && chartData !== null && (
          <div className='savings-chart'>
            <h3>
              It should take you <span>{savingTime}</span> to save your goal amount
              {interestSaved !== '0.00' && (
                <>
                  {' '}
                  - at an interest rate of <span>{interestRate}%</span> a year, you will earn{' '}
                  <span>£{interestSaved}</span> of interest
                </>
              )}
              .
            </h3>
            {chartData.steps < 2000 && dateReachedGoal !== null && (
              <h4>
                If you started today, you should reach your savings goal on{' '}
                <span>{dateReachedGoal}</span>.
              </h4>
            )}
            <div className='savings-chart-graph'>
              <Line
                options={lineOptions([])}
                data={{
                  labels: '.'
                    .repeat(chartData.steps)
                    .split('')
                    .map(
                      (_, i) => `${i} ${frequencyMap[contributionFrequency]}${i === 1 ? '' : 's'}`,
                    ),
                  datasets: [
                    {
                      label: 'Savings',
                      data: chartData.savingsData,
                      borderColor: '#DB4D75',
                      backgroundColor: '#DB4D75',
                      spanGaps: true,
                    },
                  ],
                }}
              />
            </div>
          </div>
        )}
      </Card>
      {warningText && <Callout variant='platinum-warning' bold={true} text={warningText} />}
    </div>
  )
}

export default SavingsCalculator
